import Jumbotron from '@/components/core/Jumbotron/Jumbotron';
import { Layout } from '@/components/shared/layouts/Layout/Layout';
import { linkType } from '@/components/shared/navigations/BottomNavigation/BottomNavigation';
import { ColorTypeOptions } from '@/components/shared/navigations/CoreNavigation/CoreNavigation';
import { getSafeLocale } from '@/utils/locale';
import { GetServerSidePropsContext, InferGetServerSidePropsType, NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import styles from '../../styles/explore.module.scss';
import { useTranslation } from 'next-i18next';
import ModalLink from 'next/link';
import Button from '@/components/core/Button/Button';
import Section from '@/components/core/Section/Section';
import SharePanel from '@/components/shared/share-panel/SharePanel';
import { createClient } from '@/jsonapi/createClient';
import { serializeCollectionResource } from '@/utils/serilize-resource';
import useSWRInfinite from 'swr/infinite';
import { useClient } from '@/components/shared/utilities/AppProvider/AppProvider';
import { useCallback, useMemo } from 'react';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { ICollectionResource } from '@/interfaces/ICollectionResource';
import CollectionPosts from '@/components/features/posts/CollectionPosts/CollectionPosts';
import { NextSeo } from 'next-seo';

type ExploreProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const ExplorePage: NextPage<ExploreProps> = ({ collectionsInitialData }) => {
	const { t } = useTranslation();
	const client = useClient();

	const {
		data: collections,
		error,
		setSize,
		size,
	} = useSWRInfinite(
		(pageIndex, previousPageData) => {
			if (previousPageData && !previousPageData?.links?.next) return null; // reached the end

			const pageNumber = pageIndex + 1;

			return [
				'collection/item',
				{
					include: 'favoriter',
					page: pageNumber,
					per_page: 16,
					filters: 'recent',
				},
			] as const;
		},
		async (key) => {
			const data = await client.findAll(...key);

			return serializeCollectionResource(data);
		},
		{
			fallbackData: [collectionsInitialData],
		}
	);

	const { items, isLoadingMore, isReachingEnd, meta } = useMemo(
		() => extractSWRInfiniteData(collections, error, size),
		[collections, error, size]
	);

	const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

	return (
		<>
			<NextSeo title={t('explore')} />
			<Layout
				colorTypeActive={ColorTypeOptions.PRIMARY}
				bottomNavigation={{ leftLink: linkType.VISIT, rightLink: linkType.STORIES }}
			>
				<Jumbotron
					contentClassName={styles.jumbotronContent}
					color={Jumbotron.color.PRIMARY_DARK}
					maskColor={Jumbotron.maskColor.DIMMED}
					backgroundImage="/images/collection-header.jpg"
				>
					<div className={styles.caption}>
						<div className={styles.captionLeft}>
							<h1 className={styles.captionTitle}>{t('explore-the-museum')}</h1>
							<p className={styles.captionDescription}>{t('museum-of-broken-relationships-explore')}</p>
							<ModalLink href={{ query: { open: 'about-museum' } }} shallow={true}>
								<Button size={Button.size.BIG} hollow>
									{t('read-more')}
								</Button>
							</ModalLink>
						</div>
						<div className={styles.captionRight}>
							<h2 className={styles.captionSubtitle}>{t('press')}</h2>
							<ul className={styles.captionList}>
								<li className={styles.captionListItem}>
									<ModalLink
										href={{ query: { open: 'in-press' } }}
										shallow={true}
										className={styles.captionListLinkArrow}
									>
										{t('brokenships-in-press')}
									</ModalLink>
								</li>
								<li className={styles.captionListItem}>
									<ModalLink
										href={{ query: { open: 'press-kit' } }}
										shallow={true}
										className={styles.captionListLinkArrow}
									>
										{t('press-kit')}
									</ModalLink>
								</li>
								<li className={styles.captionListItem}>
									<ModalLink
										href={{ query: { open: 'contact' } }}
										shallow={true}
										className={styles.captionListLinkArrow}
									>
										{t('contact-us')}
									</ModalLink>
								</li>
							</ul>
						</div>
					</div>
				</Jumbotron>
				<Section color="Dimmed">
					<CollectionPosts
						collections={items}
						meta={meta}
						onLoadMore={onLoadMore}
						hasMore={!isReachingEnd}
						loading={isLoadingMore}
					/>
					<SharePanel />
				</Section>
			</Layout>
		</>
	);
};

export const getServerSideProps = async ({ locale, req }: GetServerSidePropsContext) => {
	const client = createClient({ locale, cookie: req.headers.cookie });

	const collectionsData = await client.findAll<ICollectionResource>('collection/item', {
		include: 'favoriter',
		page: 1,
		per_page: 16,
		filters: 'recent',
	});

	const collectionsInitialData = serializeCollectionResource(collectionsData);

	return {
		props: {
			collectionsInitialData,
			...(await serverSideTranslations(getSafeLocale(locale), ['common'])),
		},
	};
};
export default ExplorePage;
